import { useState, type ReactNode } from 'react';
import Handleiding from './Handleiding';
import { handleiding } from '../handleiding';

/**
 * Het scherm rondom een pagina.
 *
 * ALLES IN EEN KADER. Logo en titel stonden eerst buiten de kaart, boven de
 * witte rand. Dat kostte ruimte en liet de pagina zweven; op een telefoon is
 * dat een halve bubbel aan hoogte. Nu zit alles in dezelfde omlijsting: logo
 * links, titel rechts, en het gesprek eronder.
 */
export default function Kader({
  titel,
  onderschrift,
  taalcode,
  vol = false,
  zijbalk,
  children,
}: {
  titel: string;
  onderschrift?: string;
  /**
   * De taal van de lezer. Zet de vraagtekenknop aan.
   *
   * De handleiding hoort HIER en niet per pagina: je zoekt hem juist op een
   * scherm waar je iets niet snapt, en dan wil je niet eerst ergens anders
   * heen. Laat je hem weg, dan is er geen knop - dat is voor schermen die
   * alleen een foutmelding tonen.
   */
  taalcode?: string;
  /**
   * Vult het scherm in de hoogte, met een groeiend middenstuk.
   *
   * Nodig voor de kamer: klap je daar iets in, dan hoort de RUIMTE VOOR DE
   * BERICHTEN groter te worden. Zonder dit wordt de kaart alleen korter en
   * schiet het inklappen zijn doel voorbij.
   */
  vol?: boolean;
  /**
   * Een linkerkolom, ALLEEN OP EEN BREED SCHERM (vanaf 1024 px).
   *
   * Taak 1 van 23 september 2026: op de laptop was de kamer een smalle kolom
   * van 448 px met links en rechts leegte, en Meet, kosten en instellingen
   * stonden onder het gesprek. Nu staan die links en houdt het gesprek de hele
   * hoogte.
   *
   * OP EEN TELEFOON VERANDERT ER NIETS, en dat is met opzet zo gebouwd. De
   * omhulsels krijgen daar `contents`: ze bestaan dan niet voor de indeling, en
   * de blokken staan als vanouds als losse kinderen van de kaart, de zijbalk
   * onder het gesprek. De hoogteregeling van 13 september (alleen de kaart
   * scrolt, het gespreksvak krimpt niet onder zijn inhoud) blijft zo precies
   * zoals hij was.
   */
  zijbalk?: ReactNode;
  children: ReactNode;
}) {
  const [handleidingOpen, setHandleidingOpen] = useState(false);

  return (
    <div
      className={`bg-slate-50 flex flex-col items-center px-3 ${
        vol ? 'h-dvh py-3 overflow-hidden' : 'min-h-dvh py-6'
      }`}
    >
      {/*
        `overflow-y-auto` BIJ `vol` IS EEN VANGNET EN GEEN SCROLLBALK DIE JE
        ZIET. Zolang alles past, gebeurt er niets: het gesprek groeit en de
        kaart staat stil. Past het niet - kostenbalk en meetlog open op een
        telefoon - dan scrolt de kaart in plaats van dat de blokken door elkaar
        heen lopen. Zonder dit had de buitenste `overflow-hidden` de onderkant
        er gewoon afgesneden. Gemeten 13 september 2026.
      */}
      <main
        className={`w-full max-w-md bg-white border-2 border-slate-250 rounded-2xl shadow-xs
                    animate-fade-in ${vol ? 'flex-1 min-h-0 flex flex-col overflow-y-auto p-4' : 'p-5'}
                    ${zijbalk !== undefined ? 'lg:max-w-5xl' : ''}`}
      >
        {/* Kop: merk links, waar je bent rechts. */}
        <div className="flex items-center justify-between gap-3 shrink-0 mb-3">
          <span className="flex items-center gap-2 min-w-0">
            <img
              src="/askgio-avatar.png"
              alt=""
              className="w-8 h-8 rounded-full border-2 border-gio-blue object-cover bg-white shrink-0"
            />
            <span className="text-sm font-extrabold tracking-tight text-gio-dark leading-none">
              Ask Gio <span className="text-gio-orange">Tolk</span>
            </span>
          </span>

          <span className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold text-gio-dark text-right truncate min-w-0">{titel}</h2>

            {taalcode !== undefined && (
              <button
                type="button"
                onClick={() => setHandleidingOpen(true)}
                title={handleiding(taalcode).knop}
                aria-label={handleiding(taalcode).knop}
                className="w-6 h-6 shrink-0 rounded-full border-2 border-gio-blue/40
                           bg-gio-blue/10 text-gio-blue text-xs font-bold flex items-center
                           justify-center hover:bg-gio-blue hover:text-white
                           hover:border-gio-blue transition-colors duration-155"
              >
                ?
              </button>
            )}
          </span>
        </div>

        {taalcode !== undefined && handleidingOpen && (
          <Handleiding taalcode={taalcode} opSluiten={() => setHandleidingOpen(false)} />
        )}

        {onderschrift !== undefined && (
          <p className="text-xs text-slate-550 mb-4 leading-relaxed shrink-0">{onderschrift}</p>
        )}

        {zijbalk === undefined ? (
          children
        ) : (
          <div className="contents lg:flex lg:flex-row lg:flex-1 lg:min-h-0 lg:gap-5">
            <div className="contents lg:flex lg:flex-col lg:flex-1 lg:min-w-0 lg:min-h-0 lg:overflow-y-auto">
              {children}
            </div>
            {/*
              NA het gesprek in de broncode, en met lg:order-first naar links.
              Op een telefoon (contents) volgt de zijbalk zo vanzelf ONDER het
              gesprek, waar deze blokken altijd stonden.
            */}
            <aside
              className="contents lg:block lg:order-first lg:w-72 lg:shrink-0 lg:overflow-y-auto
                         lg:pr-4 lg:border-r-2 lg:border-slate-150"
            >
              {zijbalk}
            </aside>
          </div>
        )}

        <p className="shrink-0 mt-3 pt-2 border-t-2 border-slate-150 text-[10px] text-slate-405 text-center">
          © Ginovation · Lostknowledge 2026
        </p>
      </main>
    </div>
  );
}
